import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AccountSecurity } from '@/api'
import { SettingsRow, SettingsRowSkeleton } from '@/components/SettingsRow'
import type { LocalMethodProps } from '@/features/security/local/LocalSecuritySection'

import { EmailVerificationDialog } from './EmailVerificationDialog'
import { useEmailMfa } from './useEmailMfa'

type EmailMethodProps = LocalMethodProps & {
    mfa: AccountSecurity['mfa'] | undefined
}

export const EmailMethod = ({ mfa, disabled, runAction }: EmailMethodProps) => {
    const { t } = useTranslation()
    const email = useEmailMfa()
    const [dialog, setDialog] = useState<'setup' | 'manage' | null>(null)

    if (!mfa) {
        return <SettingsRowSkeleton />
    }

    const enabled = mfa.enrolledFactors.includes('email')

    if (!mfa.enabled || (!enabled && !mfa.availableFactors.includes('email'))) {
        return null
    }

    const pending = email.enable.isPending || email.verify.isPending || email.disable.isPending

    const handleOpen = async () => {
        if (enabled) {
            setDialog('manage')
            return
        }

        try {
            await runAction(async () => {
                await email.enable.mutateAsync()
                setDialog('setup')
            })
        } catch {
            // The mutation displays setup errors in the account status banner.
        }
    }

    return (
        <>
            <SettingsRow
                icon={<EnvelopeIcon className="h-5 w-5" aria-hidden="true" />}
                title={t('security.local.email.title')}
                description={t('security.local.email.description')}
                status={enabled ? t('security.local.status.enabled') : t('security.local.status.notSetUp')}
                statusTone={enabled ? 'success' : 'neutral'}
                onAction={() => {
                    void handleOpen()
                }}
                disabled={disabled}
                pending={pending}
            />
            {dialog && (
                <EmailVerificationDialog
                    mode={dialog}
                    onClose={() => setDialog(null)}
                    onVerify={(code) =>
                        runAction(async () => {
                            await email.verify.mutateAsync(code)
                            setDialog(null)
                        })
                    }
                    onResend={() =>
                        runAction(async () => {
                            await email.enable.mutateAsync()
                        })
                    }
                    onDisable={() =>
                        runAction(async () => {
                            await email.disable.mutateAsync()
                            setDialog(null)
                        })
                    }
                />
            )}
        </>
    )
}
